import { useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { mapFieldErrors } from '@/api/fieldErrors';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { useActionError, type ActionError } from '@/components/useActionError';
import { useFieldErrors } from '@/components/useFieldErrors';
import { useFreshOnOpen } from '@/components/useFreshOnOpen';
import { inviteEmailError, looksLikeEmail } from '@/tenant/inviteGuard';
import { useTenantApi } from '@/tenant/useTenant';
import { useToast } from '@/components/useToast';
import { CardCapture } from '@/features/billing/CardCapture';
import { isCardDeclinedCode } from '@/features/billing/cardDeclined';
import { generateRequestId } from '@/utils/uuid';
import { isSubscriptionSetupConflict, subscriberErrorMessage } from './subscriberErrors';

const TRIAL_DAYS = [0, 7, 14, 30];

const FIELDS = ['email', 'trialDays'] as const;

export interface SubscribeWithCardDialogProps {
  open: boolean;
  onClose: () => void;
  onDone: (opts?: { quiet?: boolean }) => void;
}

export function SubscribeWithCardDialog(props: SubscribeWithCardDialogProps) {
  // A fresh form on every open (TMMRSL-0071). Stripe Elements owns the card step's own validation.
  return <SubscribeWithCardForm key={useFreshOnOpen(props.open)} {...props} />;
}

function SubscribeWithCardForm({ open, onClose, onDone }: SubscribeWithCardDialogProps) {
  const { t } = useTranslation();
  const showToast = useToast();
  const api = useTenantApi();
  const describe = useActionError(subscriberErrorMessage);
  const emailRef = useRef<HTMLInputElement>(null);
  const trialRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [trialDays, setTrialDays] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  const [cardEpoch, setCardEpoch] = useState(0);
  const fields = useFieldErrors(FIELDS, { email: emailRef, trialDays: trialRef }, submitting);

  /** The email check that runs before the card is tokenized. */
  function validateEmail(): boolean {
    if (looksLikeEmail(email)) return true;
    setError(null);
    fields.fail({ email: t('validation.email') });
    return false;
  }

  async function handleToken(token: string): Promise<void | 'retry'> {
    const trimmed = email.trim();
    if (!validateEmail()) return;
    setSubmitting(true);
    setError(null);
    fields.reset();
    try {
      const result = await api.subscribe({
        email: trimmed,
        cardToken: token,
        trialDays,
        idempotencyKey: generateRequestId(),
      });
      if (result.pending) {
        showToast(t('subscribers.toast.pending', { email: trimmed }));
      } else {
        showToast(t('subscribers.toast.subscribed', { email: trimmed }));
      }
      onDone();
      onClose();
    } catch (err) {
      const action = describe(err);
      const invite = inviteEmailError(err, t);
      if (invite) {
        fields.fail({ email: invite });
        return;
      }
      setError(action);
      fields.failMarked(
        mapFieldErrors(action.fieldKeys, { email: 'email', trialDays: 'trialDays' }),
        { email: t('subscribers.field.email'), trialDays: t('subscribers.field.trialDays') },
      );
      if (isSubscriptionSetupConflict(err)) {
        onDone({ quiet: true });
        setCardEpoch((epoch) => epoch + 1);
        return;
      }
      if (isCardDeclinedCode(err)) return 'retry';
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose}>
      <DialogTitle>{t('subscribers.subscribe.title')}</DialogTitle>
      <DialogContent>
        <ActionErrorAlert error={error} sx={{ mb: 2 }} />
        <TextField
          required
          fullWidth
          type="email"
          margin="dense"
          label={t('subscribers.email')}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            fields.clear('email');
          }}
          inputRef={emailRef}
          error={fields.errors.email !== undefined}
          helperText={fields.errors.email}
          disabled={submitting}
        />
        <TextField
          select
          fullWidth
          margin="dense"
          label={t('subscribers.trialDays')}
          value={trialDays}
          inputRef={trialRef}
          error={fields.errors.trialDays !== undefined}
          helperText={fields.errors.trialDays}
          onChange={(e) => {
            setTrialDays(Number(e.target.value));
            fields.clear('trialDays');
          }}
          disabled={submitting}
        >
          {TRIAL_DAYS.map((days) => (
            <MenuItem key={days} value={days}>
              {days === 0
                ? t('subscribers.trialDaysNone')
                : t('subscribers.trialDaysCount', { n: days })}
            </MenuItem>
          ))}
        </TextField>
        <CardCapture
          key={cardEpoch}
          scope="tenant"
          submitLabel={t('subscribers.subscribeWithCard')}
          busy={submitting}
          onBeforeTokenize={validateEmail}
          onToken={handleToken}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
